import type { AdminCandidateTransition, AdminDashboardData, AdminKnowledgePreview, AdminKnowledgeProvenance, AdminRegisteredUser, AdminTraceDetail, AdminTraceSummary, AdminUserActivity, AgentTrace, AnonymousSessionStatus, ChatMessage, ConversationHistory, ConversationSummary, EvaluationReport, GitHubIntegrationStatus, GitHubRepository, GraphOverviewResponse, GraphResponse, HumanApiKey, HumanApiStatus, HumanQuestion, HumanQueueView, HumanResponder, ObservabilitySummary, SourceReference, UserAccountData, UserApiKey } from '../types'

// Development must stay same-origin and use Vite's proxy.  A stale exported
// VITE_API_BASE_URL previously pointed the browser at an unused local port,
// even though both Vite and the API were healthy.  Keep the production
// variable for deployed builds and provide a deliberately named opt-in only
// when a developer really needs to bypass the proxy.
const API_BASE_URL = (
  import.meta.env.DEV
    ? (import.meta.env.VITE_DEV_API_BASE_URL || '/api/v1')
    : (import.meta.env.VITE_API_BASE_URL || '/api/v1')
).replace(/\/$/, '')
const INTEGRATIONS_BASE_URL = '/api/integrations'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message)
  }
}

async function getJson<T>(path: string, signal?: AbortSignal, headers?: HeadersInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    signal,
    credentials: 'include',
    headers,
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }))
    throw new ApiError(error.detail || 'Request failed', response.status)
  }
  return response.json() as Promise<T>
}

function cookieToken(name: string): string {
  const value = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`))
    ?.split('=')[1] || ''
  return decodeURIComponent(value)
}

function csrfToken(): string {
  return cookieToken('agentme_csrf')
}

function userCsrfToken(): string {
  return cookieToken('agentme_user_csrf')
}

function responseErrorMessage(value: unknown, fallback: string): string {
  if (!value || typeof value !== 'object') return fallback
  const payload = value as { detail?: string | { message?: string }; error?: { message?: string } }
  if (typeof payload.detail === 'string') return payload.detail
  if (payload.detail && typeof payload.detail.message === 'string') return payload.detail.message
  return payload.error?.message || fallback
}

async function integrationGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${INTEGRATIONS_BASE_URL}${path}`, {
    signal,
    credentials: 'include',
  })
  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => undefined)
    throw new ApiError(responseErrorMessage(payload, 'Integration request failed'), response.status)
  }
  return response.json() as Promise<T>
}

async function mutateJson<T>(
  path: string,
  method: 'POST' | 'PATCH' | 'DELETE',
  body?: unknown,
  additionalHeaders?: HeadersInit,
): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'X-CSRF-Token': csrfToken(),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...additionalHeaders,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' })) as {
      detail?: string
      error?: { message?: string }
    }
    throw new ApiError(error.detail || error.error?.message || 'Request failed', response.status)
  }
  return response.json() as Promise<T>
}

const HUMAN_TERMS_STORAGE_KEY = 'agentme_human_responder_terms'
let humanResponderToken = ''

function humanQueueHeaders(): Record<string, string> {
  const termsVersion = window.localStorage.getItem(HUMAN_TERMS_STORAGE_KEY) || ''
  return {
    ...(termsVersion ? { 'X-Human-Terms-Version': termsVersion } : {}),
    ...(humanResponderToken ? { 'X-Human-Responder-Token': humanResponderToken } : {}),
    ...(userCsrfToken() ? { 'X-User-CSRF-Token': userCsrfToken() } : {}),
  }
}

export function acceptedHumanResponderTerms(): string {
  return window.localStorage.getItem(HUMAN_TERMS_STORAGE_KEY) || ''
}

export function acceptHumanResponderTerms(version: string): void {
  window.localStorage.setItem(HUMAN_TERMS_STORAGE_KEY, version)
}

export const api = {
  health: (signal?: AbortSignal) =>
    getJson<{ status: string; memory_count: number; vector_store: boolean }>('/health', signal),
  graph: (signal?: AbortSignal, locale: 'en' | 'zh-CN' = 'en') =>
    getJson<GraphResponse>(`/graph?locale=${encodeURIComponent(locale)}`, signal),
  graphOverview: (signal?: AbortSignal, locale: 'en' | 'zh-CN' = 'en') =>
    getJson<GraphOverviewResponse>(`/graph/overview?locale=${encodeURIComponent(locale)}`, signal),
  evaluation: (signal?: AbortSignal) => getJson<EvaluationReport>('/evaluation/report', signal),
  observability: (signal?: AbortSignal) => getJson<ObservabilitySummary>('/observability/summary', signal),
  session: (signal?: AbortSignal) => getJson<AnonymousSessionStatus>('/session', signal),
  conversations: (limit = 20, signal?: AbortSignal) =>
    getJson<{ object: 'list'; data: ConversationSummary[] }>(
      `/conversations?limit=${limit}`,
      signal,
    ),
  conversation: (id: string, signal?: AbortSignal) =>
    getJson<ConversationHistory>(`/conversations/${encodeURIComponent(id)}`, signal),
  deleteConversation: (id: string) =>
    mutateJson<{ id: string; deleted: true }>(
      `/conversations/${encodeURIComponent(id)}`,
      'DELETE',
      undefined,
      { 'X-CSRF-Token': userCsrfToken() },
    ),
  requestUserCode: (email: string, locale: 'en' | 'zh-CN' = 'en') =>
    mutateJson<{ ok: true; message: string; expires_in_seconds: number; debug_code?: string }>(
      '/users/auth/request-code',
      'POST',
      { email, locale },
    ),
  verifyUserCode: (email: string, code: string) =>
    mutateJson<{ authenticated: true; user: { id: string; email: string } }>(
      '/users/auth/verify-code',
      'POST',
      { email, code },
    ),
  requestRegistrationCode: (
    email: string,
    password: string,
    locale: 'en' | 'zh-CN' = 'en',
    privacyNoticeVersion: string,
    privacyConsent: true,
  ) =>
    mutateJson<{ ok: true; message: string; expires_in_seconds: number; debug_code?: string }>(
      '/users/auth/register/request-code',
      'POST',
      {
        email,
        password,
        locale,
        privacy_notice_version: privacyNoticeVersion,
        privacy_consent: privacyConsent,
      },
    ),
  verifyRegistrationCode: (email: string, code: string) =>
    mutateJson<{ authenticated: true; user: { id: string; email: string } }>(
      '/users/auth/register/verify',
      'POST',
      { email, code },
    ),
  loginUser: (email: string, password: string) =>
    mutateJson<{ authenticated: true; user: { id: string; email: string } }>(
      '/users/auth/login',
      'POST',
      { email, password },
    ),
  requestPasswordResetCode: (email: string, locale: 'en' | 'zh-CN' = 'en') =>
    mutateJson<{ ok: true; message: string; expires_in_seconds: number; debug_code?: string }>(
      '/users/auth/password/request-code',
      'POST',
      { email, locale },
    ),
  resetPassword: (email: string, code: string, password: string) =>
    mutateJson<{ authenticated: true; user: { id: string; email: string } }>(
      '/users/auth/password/reset',
      'POST',
      { email, code, password },
    ),
  userAccount: (signal?: AbortSignal) => getJson<UserAccountData>('/users/account', signal),
  userCreateApiKey: async (name: string) => {
    const response = await fetch(`${API_BASE_URL}/users/account/api-keys`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': userCsrfToken(),
      },
      body: JSON.stringify({ name }),
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' })) as { detail?: string; error?: { message?: string } }
      throw new ApiError(error.detail || error.error?.message || 'Request failed', response.status)
    }
    return response.json() as Promise<UserApiKey & { key: string; shown_once: true }>
  },
  userUpdateApiKey: async (id: string, enabled: boolean) => {
    const response = await fetch(`${API_BASE_URL}/users/account/api-keys/${encodeURIComponent(id)}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': userCsrfToken() },
      body: JSON.stringify({ enabled }),
    })
    if (!response.ok) throw new ApiError('Could not update API key', response.status)
    return response.json() as Promise<UserApiKey>
  },
  userRevokeApiKey: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/users/account/api-keys/${encodeURIComponent(id)}`, {
      method: 'DELETE', credentials: 'include',
      headers: { 'X-CSRF-Token': userCsrfToken() },
    })
    if (!response.ok) throw new ApiError('Could not revoke API key', response.status)
    return response.json() as Promise<UserApiKey>
  },
  userLogout: async () => {
    const response = await fetch(`${API_BASE_URL}/users/auth/logout`, {
      method: 'POST', credentials: 'include',
      headers: { 'X-CSRF-Token': userCsrfToken() },
    })
    if (!response.ok) throw new ApiError('Logout failed', response.status)
    window.dispatchEvent(new Event('agentme-auth-changed'))
  },
  githubStatus: (signal?: AbortSignal) =>
    integrationGet<GitHubIntegrationStatus>('/github/status', signal),
  githubRepositories: (signal?: AbortSignal) =>
    integrationGet<{ object: 'list'; data: GitHubRepository[] }>('/github/repos', signal),
  githubDisconnect: async () => {
    const response = await fetch(`${INTEGRATIONS_BASE_URL}/github/disconnect`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'X-CSRF-Token': userCsrfToken() },
    })
    if (!response.ok) {
      const payload: unknown = await response.json().catch(() => undefined)
      throw new ApiError(responseErrorMessage(payload, 'Could not disconnect GitHub'), response.status)
    }
    return response.json() as Promise<{ connected: false; disconnected: boolean }>
  },
  adminProviders: (signal?: AbortSignal) => getJson<{ providers: string[] }>('/admin/auth/providers', signal),
  adminMe: (signal?: AbortSignal) => getJson<{ authenticated: true; email: string; provider: string; role: 'ADMIN' | 'VIEWER' }>('/admin/auth/me', signal),
  adminDashboard: (signal?: AbortSignal) => getJson<AdminDashboardData>('/admin/dashboard', signal),
  adminKnowledgeProvenance: (limit = 100, signal?: AbortSignal) =>
    getJson<AdminKnowledgeProvenance>(`/admin/knowledge/provenance?limit=${limit}`, signal),
  adminKnowledgePreview: () =>
    mutateJson<AdminKnowledgePreview>('/admin/knowledge/preview', 'POST'),
  adminApproveCandidate: (id: string) =>
    mutateJson<AdminCandidateTransition>(`/admin/memory/${encodeURIComponent(id)}/approve`, 'POST'),
  adminRejectCandidate: (id: string) =>
    mutateJson<AdminCandidateTransition>(`/admin/memory/${encodeURIComponent(id)}/reject`, 'POST'),
  adminPublishCandidate: (id: string) =>
    mutateJson<AdminCandidateTransition>(`/admin/memory/${encodeURIComponent(id)}/publish`, 'POST'),
  adminUnpublishCandidate: (id: string) =>
    mutateJson<AdminCandidateTransition>(`/admin/memory/${encodeURIComponent(id)}/unpublish`, 'POST'),
  adminTraces: (limit = 50, signal?: AbortSignal) => getJson<AdminTraceSummary[]>(`/admin/traces?limit=${limit}`, signal),
  adminUsers: (query = '', status = '', offset = 0, limit = 100, signal?: AbortSignal) => {
    const params = new URLSearchParams({ offset: String(offset), limit: String(limit) })
    if (query.trim()) params.set('query', query.trim())
    if (status) params.set('status', status)
    return getJson<{ object: 'list'; data: AdminRegisteredUser[]; total: number; offset: number; limit: number }>(`/admin/users?${params}`, signal)
  },
  adminUserActivity: (id: string, limit = 100, signal?: AbortSignal) =>
    getJson<AdminUserActivity>(
      `/admin/users/${encodeURIComponent(id)}/activity?limit=${limit}`,
      signal,
    ),
  adminUpdateUser: (id: string, status: 'active' | 'suspended') =>
    mutateJson<{ id: string; email: string; status: string }>(
      `/admin/users/${encodeURIComponent(id)}`,
      'PATCH',
      { status },
    ),
  adminSetUserUnlimited: (id: string, unlimited_questions: boolean) =>
    mutateJson<{ id: string; email: string; status: string; unlimited_questions: boolean }>(
      `/admin/users/${encodeURIComponent(id)}`,
      'PATCH',
      { unlimited_questions },
    ),
  adminRevokeUserSessions: (id: string) =>
    mutateJson<{ id: string; revoked_sessions: number }>(
      `/admin/users/${encodeURIComponent(id)}/revoke-sessions`, 'POST',
    ),
  adminRevokeUserApiKeys: (id: string) =>
    mutateJson<{ id: string; revoked_api_keys: number }>(
      `/admin/users/${encodeURIComponent(id)}/revoke-api-keys`, 'POST',
    ),
  adminUnlockUser: (id: string) =>
    mutateJson<{ id: string; unlocked: true }>(
      `/admin/users/${encodeURIComponent(id)}/unlock`, 'POST',
    ),
  adminTrace: (traceId: string, signal?: AbortSignal) => getJson<AdminTraceDetail>(`/admin/traces/${encodeURIComponent(traceId)}`, signal),
  humanStatus: async (signal?: AbortSignal) => {
    const result = await getJson<HumanApiStatus>('/human-api/status', signal)
    humanResponderToken = result.responder_token || ''
    return result
  },
  humanQuestions: (view: HumanQueueView, signal?: AbortSignal) =>
    getJson<{ object: 'list'; data: HumanQuestion[] }>(
      `/human-api/questions?view=${view}`,
      signal,
      humanQueueHeaders(),
    ),
  humanQuestion: (id: string, signal?: AbortSignal) =>
    getJson<HumanQuestion>(
      `/human-api/questions/${encodeURIComponent(id)}`,
      signal,
      humanQueueHeaders(),
    ),
  humanHeartbeat: () => mutateJson<{ ok: true }>(
    '/human-api/heartbeat',
    'POST',
    undefined,
    humanQueueHeaders(),
  ),
  humanClaim: (id: string) =>
    mutateJson<HumanQuestion>(
      `/human-api/questions/${encodeURIComponent(id)}/claim`,
      'POST',
      undefined,
      humanQueueHeaders(),
    ),
  humanRelease: (id: string) =>
    mutateJson<{ ok: true }>(
      `/human-api/questions/${encodeURIComponent(id)}/release`,
      'POST',
      undefined,
      humanQueueHeaders(),
    ),
  humanAnswer: (id: string, content: string) =>
    mutateJson<{ ok: true; completion_id: string }>(
      `/human-api/questions/${encodeURIComponent(id)}/answer`,
      'POST',
      { content },
      humanQueueHeaders(),
    ),
  humanCancel: (id: string) =>
    mutateJson<{ ok: true }>(`/human-api/questions/${encodeURIComponent(id)}/cancel`, 'POST'),
  humanApiKeys: (signal?: AbortSignal) =>
    getJson<{ object: 'list'; data: HumanApiKey[] }>('/human-api/api-keys', signal),
  humanCreateApiKey: (name: string, rateLimit: number) =>
    mutateJson<HumanApiKey & { key: string; shown_once: true }>('/human-api/api-keys', 'POST', {
      name,
      rate_limit_per_minute: rateLimit,
    }),
  humanUpdateApiKey: (id: string, enabled: boolean) =>
    mutateJson<HumanApiKey>(`/human-api/api-keys/${encodeURIComponent(id)}`, 'PATCH', { enabled }),
  humanRevokeApiKey: (id: string) =>
    mutateJson<HumanApiKey>(`/human-api/api-keys/${encodeURIComponent(id)}`, 'DELETE'),
  humanResponders: (signal?: AbortSignal) =>
    getJson<{ object: 'list'; data: HumanResponder[] }>('/human-api/responders', signal),
  humanGrantResponder: (userId: string) =>
    mutateJson<{ id: string }>('/human-api/responders', 'POST', { user_id: userId }),
  humanUpdateResponder: (id: string, enabled: boolean) =>
    mutateJson<{ id: string }>(`/human-api/responders/${encodeURIComponent(id)}`, 'PATCH', { enabled }),
  humanRevokeResponder: (id: string) =>
    mutateJson<{ ok: true }>(`/human-api/responders/${encodeURIComponent(id)}`, 'DELETE'),
  humanUpdateSettings: (enabled: boolean) =>
    mutateJson<{ enabled: boolean }>('/human-api/settings', 'PATCH', { enabled }),
  captureAnalytics: async (payload: {
    event_id: string
    event_type: 'page_view' | 'engaged_visit' | 'chat_opened'
    path: string
    referrer: string
    duration_ms?: number
    visible?: boolean
    interacted?: boolean
    utm_source?: string
    utm_medium?: string
    utm_campaign?: string
  }) => {
    await fetch(`${API_BASE_URL}/analytics/events`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  },
  adminLoginUrl: (provider: string) => `${API_BASE_URL}/admin/auth/login/${encodeURIComponent(provider)}`,
  adminLogout: async () => {
    const response = await fetch(`${API_BASE_URL}/admin/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-CSRF-Token': csrfToken() },
    })
    if (!response.ok) throw new ApiError('Logout failed', response.status)
  },
}

interface StreamCallbacks {
  onStage: (label: string) => void
  onSources: (sources: SourceReference[]) => void
  onMetadata: (metadata: {
    grounded: boolean
    trace_id: string
    session_id: string
    conversation_id: string
    agent_trace: AgentTrace[]
    reflection: { approved: boolean; revised: boolean; faithfulness: number; completeness: number; issues: string[] }
    answer_mode: 'GROUNDED' | 'PARTIALLY_GROUNDED' | 'GENERAL' | 'UNKNOWN_PERSONAL'
    public_trace: ChatMessage['publicTrace']
  }) => void
  onToken: (token: string) => void
  onError: (detail: string) => void
  onDone: () => void
}

const RETRYABLE_STREAM_STATUSES = new Set([500, 502, 503, 504])

async function waitForRetry(signal: AbortSignal, milliseconds: number): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, milliseconds)
    const onAbort = () => {
      window.clearTimeout(timeout)
      reject(new DOMException('Request aborted', 'AbortError'))
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

async function openChatStream(
  body: string,
  signal: AbortSignal,
  requestId: string,
): Promise<Response> {
  let lastError: unknown
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await fetch(`${API_BASE_URL}/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          'X-Request-ID': requestId,
        },
        body,
        signal,
        credentials: 'include',
      })
      if (response.ok || !RETRYABLE_STREAM_STATUSES.has(response.status) || attempt === 1) {
        return response
      }
      await response.body?.cancel()
    } catch (error) {
      if ((error as Error).name === 'AbortError' || attempt === 1) throw error
      lastError = error
    }
    await waitForRetry(signal, 650)
  }
  throw lastError instanceof Error ? lastError : new ApiError('Unable to open interview stream.')
}

export async function streamChat(
  message: string,
  history: Array<{ role: 'user' | 'assistant'; content: string }>,
  callbacks: StreamCallbacks,
  signal: AbortSignal,
  imageDataUrl?: string,
  sessionId?: string,
  conversationId?: string,
): Promise<void> {
  const body = JSON.stringify({
    message,
    history,
    session_id: sessionId || null,
    conversation_id: conversationId || null,
    image_data_url: imageDataUrl || null,
  })
  const requestId = crypto.randomUUID()
  const response = await openChatStream(body, signal, requestId)
  if (!response.ok || !response.body) {
    const error = await response.json().catch(() => ({ detail: 'Agent-Me is unavailable.' }))
    throw new ApiError(error.detail || 'Agent-Me is unavailable.', response.status)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let streamCompleted = false
  let answerCompleted = false

  const parseBlock = (block: string) => {
    let event = 'message'
    const data: string[] = []
    block.split('\n').forEach((line) => {
      if (line.startsWith('event:')) event = line.slice(6).trim()
      if (line.startsWith('data:')) data.push(line.slice(5).trim())
    })
    if (!data.length) return
    const payload = JSON.parse(data.join('\n')) as unknown
    if (event === 'stage') callbacks.onStage((payload as { label: string }).label)
    if (event === 'sources') callbacks.onSources(payload as SourceReference[])
    if (event === 'metadata') {
      callbacks.onMetadata(payload as {
        grounded: boolean
        trace_id: string
        session_id: string
        conversation_id: string
        agent_trace: AgentTrace[]
        reflection: { approved: boolean; revised: boolean; faithfulness: number; completeness: number; issues: string[] }
        answer_mode: 'GROUNDED' | 'PARTIALLY_GROUNDED' | 'GENERAL' | 'UNKNOWN_PERSONAL'
        public_trace: ChatMessage['publicTrace']
      })
    }
    if (event === 'token') callbacks.onToken(payload as string)
    if (event === 'error') callbacks.onError((payload as { detail: string }).detail)
    if (event === 'answer_completed') answerCompleted = true
    if (event === 'done') {
      streamCompleted = true
      callbacks.onDone()
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    buffer = `${buffer}${decoder.decode(value, { stream: !done })}`
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
    const blocks = buffer.split('\n\n')
    buffer = blocks.pop() || ''
    blocks.forEach(parseBlock)
    if (done) break
  }
  if (buffer.trim()) parseBlock(buffer)
  if (!streamCompleted && !answerCompleted) {
    throw new ApiError('The interview stream ended before the answer was completed.')
  }
  if (!streamCompleted) callbacks.onDone()
}
